import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, CircularProgress, Link as MuiLink } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { fetchPublicDWStateElections } from '../utils/democracyWorksApi';
import { getStateName } from '../utils/stateLegislationApi';
import {
  extractMeasuresFromElections,
  formatMeasureDisplayName,
  getMeasureJurisdictionLabel,
  formatElectionDate,
  formatBallotStatus,
  sanitizeDwHtml,
  getMeasureSourceUrl,
  buildBallotListSearchParams,
  BALLOT_SCOPE_DEFAULT,
} from '../utils/ballotMeasuresApi';

const PRIMARY = '#047857';

function Section({ title, children }) {
  if (!children) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a', mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function BallotMeasureDetailPage() {
  const { stateCode, measureId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const decodedId = decodeURIComponent(measureId || '');

  const [measure, setMeasure] = useState(location.state?.measure || null);
  const [loading, setLoading] = useState(!location.state?.measure);
  const [error, setError] = useState(null);

  const backListUrl = useMemo(() => {
    // The card carries the list's filters in the search string, so Back returns
    // to the same scope/town/page the user came from.
    const carried = (location.search || '').replace(/^\?/, '');
    if (carried) return `/bills?${carried}`;
    const upper = (stateCode || '').toUpperCase();
    const params = buildBallotListSearchParams({
      stateCode: upper,
      scope: BALLOT_SCOPE_DEFAULT,
      query: '',
      page: 1,
    });
    return `/bills?${params.toString()}`;
  }, [stateCode, location.search]);

  useEffect(() => {
    if (location.state?.measure?.id === decodedId) {
      setMeasure(location.state.measure);
      setLoading(false);
      return;
    }

    let isCurrent = true;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const upper = (stateCode || '').toUpperCase();
        const res = await fetchPublicDWStateElections(upper);
        if (!isCurrent) return;
        if (res.error) {
          setError(res.error);
          return;
        }
        const measures = extractMeasuresFromElections(res.elections, upper);
        const found = measures.find((m) => m.id === decodedId);
        if (found) {
          setMeasure(found);
        } else {
          setError('This ballot measure could not be found. It may no longer be on an upcoming election.');
        }
      } catch (err) {
        if (!isCurrent) return;
        setError(err.message || 'Failed to load ballot measure.');
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    load();
    return () => {
      isCurrent = false;
    };
  }, [stateCode, decodedId, location.state?.measure]);

  const sourceUrl = measure ? getMeasureSourceUrl(measure) : null;
  const stateName = getStateName((stateCode || '').toUpperCase()) || stateCode;

  if (loading) {
    return (
      <Box sx={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: PRIMARY }} />
      </Box>
    );
  }

  if (error || !measure) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
        <Button
          component={Link}
          to={backListUrl}
          startIcon={<ArrowBackRoundedIcon />}
          sx={{ mb: 2, textTransform: 'none', color: '#334155', fontWeight: 600 }}
        >
          Back to ballot measures
        </Button>
        <Typography sx={{ color: '#b91c1c', fontWeight: 600 }}>{error || 'Measure not found.'}</Typography>
      </Box>
    );
  }

  const title = formatMeasureDisplayName(measure);
  const jurisdiction = getMeasureJurisdictionLabel(measure);
  const electionDate = formatElectionDate(measure.electionDate);
  const statusLabel = formatBallotStatus(measure);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, md: 3 }, pb: 6 }}>
      <Button
        onClick={() => navigate(backListUrl)}
        startIcon={<ArrowBackRoundedIcon />}
        sx={{ mb: 2, textTransform: 'none', color: '#334155', fontWeight: 600 }}
      >
        Back to ballot measures
      </Button>

      <Typography component="h1" sx={{ fontSize: { xs: '1.35rem', md: '1.6rem' }, fontWeight: 800, color: '#0f172a', mb: 1.5, lineHeight: 1.3 }}>
        {title}
      </Typography>

      {measure.name && measure.name !== title && (
        <Typography sx={{ fontSize: '0.95rem', color: '#475569', mb: 2 }}>
          {measure.name}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <PlaceRoundedIcon sx={{ fontSize: 18, color: '#475569' }} />
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155' }}>
            {jurisdiction || stateName}
          </Typography>
        </Box>
        {electionDate && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <EventNoteRoundedIcon sx={{ fontSize: 18, color: '#475569' }} />
            <Typography sx={{ fontSize: '0.9rem', color: '#334155' }}>
              Election: {electionDate}
              {measure.electionName ? ` · ${measure.electionName}` : ''}
            </Typography>
          </Box>
        )}
        {measure.type && (
          <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
            Type: {measure.type}
          </Typography>
        )}
      </Box>

      {statusLabel && (
        <Section title="Ballot status">
          <Typography sx={{ fontSize: '0.95rem', color: '#334155' }}>{statusLabel}</Typography>
        </Section>
      )}

      {measure.summary && (
        <Section title="Summary">
          <Box
            sx={{
              fontSize: '0.95rem',
              color: '#334155',
              lineHeight: 1.6,
              '& p': { m: 0, mb: 1 },
              '& a': { color: PRIMARY, fontWeight: 600 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeDwHtml(measure.summary) }}
          />
        </Section>
      )}

      {measure.ballotQuestion && (
        <Section title="Ballot question">
          <Box
            sx={{
              fontSize: '0.95rem',
              color: '#334155',
              lineHeight: 1.6,
              p: 2,
              borderRadius: '10px',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              '& p': { m: 0, mb: 1 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeDwHtml(measure.ballotQuestion) }}
          />
        </Section>
      )}

      {(measure.yesVote || measure.noVote) && (
        <Section title="What a yes or no vote means">
          {measure.yesVote && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>Yes vote</Typography>
              <Box
                sx={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.55, '& p': { m: 0 } }}
                dangerouslySetInnerHTML={{ __html: sanitizeDwHtml(measure.yesVote) }}
              />
            </Box>
          )}
          {measure.noVote && (
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}>No vote</Typography>
              <Box
                sx={{ fontSize: '0.9rem', color: '#334155', lineHeight: 1.55, '& p': { m: 0 } }}
                dangerouslySetInnerHTML={{ __html: sanitizeDwHtml(measure.noVote) }}
              />
            </Box>
          )}
        </Section>
      )}

      {sourceUrl && (
        <Section title="Authoritative source">
          <MuiLink
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              color: PRIMARY,
              fontWeight: 700,
              fontSize: '0.95rem',
            }}
          >
            View full measure details
            <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
          </MuiLink>
        </Section>
      )}

      <Typography sx={{ fontSize: 12, color: '#64748b', mt: 4 }}>
        Ballot data sourced from Ballotpedia via Democracy Works. This page reflects published data for
        upcoming {stateName} elections and is not your personalized ballot.
      </Typography>
    </Box>
  );
}
